import { v4 as uuidv4 } from "uuid";
import { format, parseISO } from "date-fns";
import { QRModel } from "src/app/store/models/site-qr.model";

export const DEFAULT_SIZE = "40px";
export const TEXT_COLOR = "#FDD100";
export const BORDER_COLOR = "#009146";
export const DROPPED_COLOR = "#ffffff";
export const RED_ICON = "../../../../assets/zoneQr/qr_red_re.png";
export const DEFAULT_ICON = "../../../../assets/zoneQr/qr.svg";
export const GREEN_ICON = "../../../../assets/zoneQr/qr_green.svg";
export const ORANGE_ICON = "../../../../assets/zoneQr/qr_or.png";

injectDropAnimationCSS();

export async function createCustomQRMarker(
  qr: QRModel | undefined,
  isOutsideZone: boolean,
): Promise<{
  mapMarker: HTMLDivElement;
  markerContainer: HTMLDivElement;
  markerImage: HTMLImageElement;
  qrLabel: HTMLDivElement;
}> {
  const mapMarker = document.createElement("div");
  const markerImage = document.createElement("img");
  const markerContainer = document.createElement("div");
  const qrLabel = document.createElement("div");

  // --------------------------
  // Determine icon based on last_scanned
  // --------------------------
  const useIcon = determineQRMarkerColor(qr);

  // --------------------------
  // Label (zoneArea)
  // --------------------------
  applyQRLabelStyles(qrLabel, qr?.zoneArea ?? "");

  // --------------------------
  // Marker image
  // --------------------------
  applyMarkerImageStyles(markerImage, useIcon);

  // --------------------------
  // Marker base style
  // --------------------------
  applyQRMarkerBaseStyles(mapMarker);

  // --------------------------
  // Container (wrapper)
  // --------------------------
  applyContainerStyles(markerContainer);

  // Append elements
  mapMarker.appendChild(markerImage);
  markerContainer.appendChild(qrLabel);
  markerContainer.appendChild(mapMarker);

  // --------------------------
  // Warning icon if outside zone
  // --------------------------
  if (!qr?.zone || isOutsideZone) {
    const warningIcon = createWarningIcon();
    applyWarningIconStyles(warningIcon);
    markerContainer.appendChild(warningIcon);
  }
  addDropAnimation(markerContainer);
  return { mapMarker, markerContainer, markerImage, qrLabel };
}

function determineQRMarkerColor(qr: QRModel | undefined): string {
  if (!qr?.last_scanned) return RED_ICON;

  try {
    const scannedDate = parseISO(qr.last_scanned);
    const today = new Date();
    const diffDays = Math.floor(
      (today.getTime() - scannedDate.getTime()) / 86400000,
    );

    if (diffDays > 15) return RED_ICON;
    if (diffDays > 7) return ORANGE_ICON;
    if (diffDays > 0) return GREEN_ICON;
    return DEFAULT_ICON;
  } catch {
    return RED_ICON;
  }
}
function applyQRLabelStyles(label: HTMLElement, text: string) {
  label.innerText = text;

  // Positioning
  label.style.position = "absolute";
  label.style.top = "50%";
  label.style.left = "100%";
  label.style.transform = "translateY(-50%)";
  label.style.marginLeft = "6px";
  label.style.whiteSpace = "nowrap";
  label.style.pointerEvents = "auto";

  // Font
  label.style.fontFamily = "Roboto, sans-serif";
  label.style.fontSize = "14px";
  // label.style.fontWeight = "500";
  label.style.lineHeight = "normal";
  label.style.textAlign = "center";

  // ✅ IMPORTANT PART
  label.style.color = "#FFFFFF";
  label.style.fontWeight = "700";

  /* REMOVE stroke entirely */
  label.style.webkitTextStrokeWidth = "0";

  /* Use multi-directional shadow outline */
  label.style.textShadow = `
  -2px -2px 0 #000,
   2px -2px 0 #000,
  -2px  2px 0 #000,
   2px  2px 0 #000,
   0px -2px 0 #000,
   0px  2px 0 #000,
  -2px  0px 0 #000,
   2px  0px 0 #000
`;

  // No background
  label.style.background = "transparent";
  label.style.border = "none";
  label.style.boxShadow = "none";
  label.style.padding = "0";
}

function applyMarkerImageStyles(
  markerImage: HTMLImageElement,
  useIcon: string,
) {
  markerImage.src = useIcon;
  markerImage.style.width = "100%";
  markerImage.style.height = "100%";
  markerImage.style.objectFit = "cover";
  markerImage.style.pointerEvents = "auto";
}
function applyQRMarkerBaseStyles(mapMarker: HTMLElement) {
  mapMarker.className = "mapMarker";
  mapMarker.setAttribute("id", uuidv4());
  mapMarker.style.padding = "5px";
  mapMarker.style.borderRadius = "50%";
  mapMarker.style.width = DEFAULT_SIZE;
  mapMarker.style.height = DEFAULT_SIZE;
  mapMarker.style.backgroundColor = "#ffffff";
  mapMarker.style.border = `2px solid ${BORDER_COLOR}`;
  mapMarker.style.position = "relative";
  mapMarker.style.pointerEvents = "auto";

  addCompletedMarkerPseudoElement(BORDER_COLOR, mapMarker);
}
function applyContainerStyles(container: HTMLElement) {
  container.style.position = "relative";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.pointerEvents = "auto";
  container.style.touchAction = "manipulation";
}
function createWarningIcon(): HTMLElement {
  const warning = document.createElement("div");
  warning.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#FF0000"/>
      <line x1="12" y1="6" x2="12" y2="13" stroke="#FFF" stroke-width="2.5" stroke-linecap="round"/>
      <circle cx="12" cy="17.5" r="1.5" fill="#FFF"/>
    </svg>
  `;
  return warning;
}
function applyWarningIconStyles(icon: HTMLElement) {
  icon.style.position = "absolute";
  icon.style.top = "-15px";
  icon.style.left = "50%";
  icon.style.transform = "translateX(-50%)";
  icon.style.zIndex = "10";
  icon.style.pointerEvents = "none";
  icon.style.filter = "drop-shadow(0 0 4px rgba(0,0,0,0.25))";
}

// function applyStylesForCompletedMarker(
//   mapMarker: HTMLElement,
//   isCompleted: boolean,
//   markerStatus: HTMLElement,
// ) {
//   mapMarker.style.padding = "5px";
//   mapMarker.style.borderRadius = "8px";
//   mapMarker.style.width = DEFAULT_SIZE;
//   mapMarker.style.position = "relative";
//   mapMarker.style.height = DEFAULT_SIZE;
//   mapMarker.style.border = DROPPED_COLOR;
//   mapMarker.style.backgroundColor = "#ffffff";
//   mapMarker.style.color = DROPPED_COLOR;

//   if (!isCompleted) {
//     addCompletedMarkerPseudoElement(DROPPED_COLOR, mapMarker);
//   } else {
//     addCompletedMarkerPseudoElement(DROPPED_COLOR, mapMarker);
//   }
// }

function addCompletedMarkerPseudoElement(
  color: string,
  mapMarker: HTMLElement,
) {
  const markerAfter = document.createElement("div");
  markerAfter.style.width = "0";
  markerAfter.style.content = "";
  markerAfter.style.height = "0";
  markerAfter.style.left = "50%";
  markerAfter.style.top = "100%";
  markerAfter.className = "markerAfter";
  markerAfter.style.position = "absolute";
  markerAfter.style.transform = "translate(-50%, 0)";
  markerAfter.style.borderTop = `8px solid ${color}`;
  markerAfter.style.borderLeft = "5px solid transparent";
  markerAfter.style.borderRight = "5px solid transparent";
  mapMarker.appendChild(markerAfter);
}

function addDropAnimation(content: HTMLElement) {
  content.classList.add("appear");

  content.addEventListener(
    "animationend",
    () => {
      content.classList.remove("appear");
    },
    { passive: true },
  );
}

function injectDropAnimationCSS() {
  const style = document.createElement("style");
  style.textContent = `
    .appear {
      animation: appearAnimation 0.6s ease-out forwards;
    }

    @keyframes appearAnimation {
      0% {
        opacity: 0;
        transform: scale(0.8);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }
  `;
  document.head.appendChild(style);
}

export function createMainLocationToggleElement(
  initialValue: boolean,
  onToggle: (isMain: boolean) => void,
  onDelete?: () => void,
): HTMLDivElement {
  const wrapper = document.createElement("div");
  wrapper.style.position = "relative";
  wrapper.style.display = "inline-block";

  const toggleContainer = document.createElement("div");
  toggleContainer.style.display = "flex";
  toggleContainer.style.flexDirection = "column";
  toggleContainer.style.alignItems = "center";
  toggleContainer.style.justifyContent = "start";
  toggleContainer.style.backgroundColor = "white";
  toggleContainer.style.padding = "8px 12px";
  toggleContainer.style.borderRadius = "1px";
  toggleContainer.style.boxShadow = "0px 4px 10px rgba(0,0,0,0.2)";
  toggleContainer.innerHTML = `
    <label style="font-weight: bold; color: #000000; margin-bottom: 6px; font-size: 14px;">Is this main boundary?</label>
    <div style="width: 100%; display: flex; align-items: center; justify-content: start; gap: 4px;">
      <span style="font-size: 14px;">No</span>
      <label class="switch">
        <input type="checkbox" id="mainLocationToggle" ${initialValue ? "checked" : ""}>
        <span class="slider round"></span>
      </label>
      <span style="font-size: 14px;">Yes</span>
    </div>
    <div class="arrow-down"></div>
  `;

  // Create the floating delete button
  const deleteButton = document.createElement("button");
  deleteButton.id = "deleteButton";
  deleteButton.title = "Delete";
  deleteButton.innerHTML = `
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white">
    <path d="M3 6h18v2H3V6zm2 3h14l-1.5 13h-11L5 9zm4 2v9h2v-9H9zm4 0v9h2v-9h-2z"/>
  </svg>
`;
  deleteButton.style.position = "absolute";
  deleteButton.style.top = "14px";
  deleteButton.style.right = "-34px";
  deleteButton.style.backgroundColor = "#F00014";
  deleteButton.style.color = "white";
  deleteButton.style.border = "none";
  deleteButton.style.borderRadius = "50%";
  deleteButton.style.width = "28px";
  deleteButton.style.height = "28px";
  deleteButton.style.cursor = "pointer";
  deleteButton.style.display = "flex";
  deleteButton.style.alignItems = "center";
  deleteButton.style.justifyContent = "center";
  deleteButton.style.fontSize = "14px";
  deleteButton.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";

  // Prevent map interaction
  ["click", "mousedown", "dblclick", "pointerdown", "touchstart"].forEach(
    (eventName) => {
      toggleContainer.addEventListener(eventName, (e) => e.stopPropagation(), {
        passive: true,
      });
      deleteButton.addEventListener(eventName, (e) => e.stopPropagation(), {
        passive: true,
      });
    },
  );

  // Add styles only once
  const styleId = "custom-toggle-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .switch {
        position: relative;
        display: inline-block;
        width: 36px;
        height: 18px;
      }
      .switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      .slider {
        position: absolute;
        cursor: pointer;
        top: 4px;
        left: 0;
        right: 0;
        bottom: 2px;
        background-color: #FDE3E5;
        transition: .4s;
        border-radius: 18px;
      }
      .slider:before {
        position: absolute;
        content: "";
        height: 17px;
        width: 17px;
        left: 0px;
        bottom: -2px;
        background-color: #F00014;
        transition: .4s;
        border-radius: 50%;
      }
      input:checked + .slider {
        background-color: #BBF5D8;
      }
      input:checked + .slider:before {
        background-color: #009146;
        transform: translateX(18px);
      }
      .arrow-down {
        width: 12px;
        height: 12px;
        background: white;
        transform: rotate(45deg);
        position: absolute;
        bottom: -6px;
        left: calc(50% - 6px);
        box-shadow: 1px 4px 1px rgba(0,0,0,0.1);
        border-radius: 2px;
      }
    `;
    document.head.appendChild(style);
  }

  const toggleInput = toggleContainer.querySelector<HTMLInputElement>(
    "#mainLocationToggle",
  );
  toggleInput?.addEventListener(
    "change",
    () => {
      onToggle(toggleInput.checked);
    },
    { passive: true },
  );

  if (onDelete) {
    deleteButton?.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
        onDelete();
      },
      { passive: true },
    );
  }

  wrapper.appendChild(toggleContainer);
  wrapper.appendChild(deleteButton);

  return wrapper;
}

export function createMainLocationLabelElement(title?: string): HTMLDivElement {
  const hasTitle = !!title?.trim();
  const labelText = hasTitle
    ? `<div><strong>${title}</strong></div><div style="font-weight: 400;">(Main location)</div>`
    : `<div><strong>Main location</strong></div>`;

  const container = document.createElement("div");
  container.className = "main-location-label";
  container.innerHTML = `
    <div class="label-box">
      ${labelText}
    </div>
    <div class="arrow-down"></div>
  `;

  // Inject label styles once
  const styleId = "main-location-label-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .main-location-label {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        pointer-events: none;
      }

      .main-location-label .label-box {
        background: white;
        border-radius: 4px;
        padding: 9px 13px;
        font-size: 14px;
        color: #000;
        box-shadow: 0 0px 2px rgba(0, 0, 0, 0.3);
        text-align: center;
        font-weight: 500;
        border: 1px solid #ccc;
        white-space: nowrap;
        pointer-events: auto;
      }

      .main-location-label .arrow-down {
        width: 12px;
        height: 12px;
        background: white;
        transform: rotate(45deg);
        position: absolute;
        bottom: -5px;
        box-shadow: 1px 0px -1px 1px rgba(0,0,0,0.3);
        border-radius: 2px;
        z-index: 0;
      }
    `;
    document.head.appendChild(style);
  }

  return container;
}

export function createSecondaryLocationLabelElement(
  title?: string,
): HTMLDivElement {
  const hasTitle = !!title?.trim();
  const labelText = hasTitle
    ? `<div><strong>${title}</strong></div><div style="font-weight: 400;">(Secondary location)</div>`
    : `<div><strong>Secondary location</strong></div>`;

  const container = document.createElement("div");
  container.className = "main-location-label";
  container.innerHTML = `
    <div class="label-box">
      ${labelText}
    </div>
    <div class="arrow-down"></div>
  `;

  // Inject label styles once
  const styleId = "main-location-label-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      .main-location-label {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        pointer-events: none;
      }

      .main-location-label .label-box {
        background: white;
        border-radius: 4px;
        padding: 9px 13px;
        font-size: 14px;
        color: #000;
        box-shadow: 0 0px 2px rgba(0, 0, 0, 0.3);
        text-align: center;
        font-weight: 500;
        border: 1px solid #ccc;
        white-space: nowrap;
        pointer-events: auto;
      }

      .main-location-label .arrow-down {
        width: 12px;
        height: 12px;
        background: white;
        transform: rotate(45deg);
        position: absolute;
        bottom: -5px;
        box-shadow: 1px 0px -1px 1px rgba(0,0,0,0.3);
        border-radius: 2px;
        z-index: 0;
      }
    `;
    document.head.appendChild(style);
  }

  return container;
}

export function createZoneNameButton(zoneName: string): HTMLDivElement {
  const zoneNameButton = document.createElement("div");
  zoneNameButton.innerText = zoneName;
  zoneNameButton.style.display = "flex";
  zoneNameButton.style.padding = "0px";
  zoneNameButton.style.margin = "0px";
  zoneNameButton.style.fontSize = "16px";
  zoneNameButton.style.fontFamily =
    "Nunito Bold, 'Nunito Medium', Arial, sans-serif";
  zoneNameButton.style.fontWeight = "350";
  zoneNameButton.style.color = "#ffffff";
  zoneNameButton.style.backgroundColor = "transparent";
  zoneNameButton.style.boxShadow = "none";
  zoneNameButton.style.borderRadius = "0";
  zoneNameButton.style.alignItems = "center";
  zoneNameButton.style.justifyContent = "center";
  zoneNameButton.style.transform = "translate(-50%, -50%)";
  zoneNameButton.style.position = "absolute";
  const outlineShadows = [
    "-1px -1px 0 #000",
    "1px -1px 0 #000",
    "-1px 1px 0 #000",
    "1px 1px 0 #000",
    "0 -1px 0 #000",
    "0 1px 0 #000",
    "-1px 0 0 #000",
    "1px 0 0 #000",
  ].join(", ");
  zoneNameButton.style.setProperty("text-shadow", outlineShadows);
  // zoneNameButton.style.setProperty("-webkit-text-stroke", "0");

  return zoneNameButton;
}
